import { useEffect, useMemo, useState } from "react";
import Papa from "papaparse";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { UploadSimple, Download, CheckCircle, XCircle, MinusCircle, FileCsv, Warning, Info } from "@phosphor-icons/react";

const SHOPIFY_HEADERS_REQUIRED = ["Handle", "Title", "Vendor", "Variant SKU", "Option1 Value"];
const SHOPIFY_TEMPLATE = `Handle,Title,Body (HTML),Vendor,Product Category,Type,Tags,Published,Option1 Name,Option1 Value,Option2 Name,Option2 Value,Option3 Name,Option3 Value,Variant SKU,Variant Grams,Variant Inventory Tracker,Variant Inventory Qty,Variant Inventory Policy,Variant Fulfillment Service,Variant Price,Variant Compare At Price
teckwrap-gunsmoke-grey,Teckwrap Gunsmoke Grey,Subtle texture with a soft metallic glow.,Teckwrap,Vehicle Wraps,Vinyl Wrap,"Teckwrap, Vinyl Wrap, Eggshell Metallic, Satin",TRUE,Size,1.52m x 1.05m,,,,,TWVWECH02152105,1600,shopify,,deny,manual,84,
teckwrap-gunsmoke-grey,,,,,,,,,1.52m x 18.05m,,,,,TWVWECH021521805,11800,shopify,,deny,manual,1044,`;

const SIMPLE_TEMPLATE = `manufacturer_name,series_name,name,base_sku,color_hex,inventory_type,discontinued,low_stock_threshold_meters,cost_per_meter,price_per_meter
3M,2080,Gloss Riviera Blue,3M-2080-GRB,#1d4ed8,rolls,no,5,18.50,42.00
Avery Dennison,SW900,Gloss Cherry,AD-SW900-GC,#b91c1c,rolls,no,5,17.00,40.00
Emperor,Tools,Squeegee Pro,EMP-SQ-PRO,,count,no,,,`;

function parseCSVRows(text) {
  // Strip UTF-8 BOM and use Papa Parse — industry-standard, handles RFC 4180 edge cases:
  // quoted fields with commas, embedded newlines, escaped quotes, CRLF/LF line endings.
  const cleaned = text.replace(/^\ufeff/, "");
  const result = Papa.parse(cleaned, {
    header: true,
    skipEmptyLines: "greedy",
    transformHeader: (h) => h.trim(),
  });
  const headers = (result.meta.fields || []).map((h) => h.trim());
  const rows = (result.data || []).map((r) => {
    const obj = {};
    for (const h of headers) obj[h] = (r[h] ?? "").toString().trim();
    return obj;
  });
  return { headers, rows, parseErrors: result.errors || [] };
}

function detectFormat(headers) {
  const h = headers.map((s) => s.toLowerCase());
  const hasShopify = SHOPIFY_HEADERS_REQUIRED.every((req) => h.includes(req.toLowerCase()));
  if (hasShopify) return "shopify";
  if (h.includes("manufacturer_name") && h.includes("base_sku")) return "simple";
  return null;
}

function shopifyPreview(rows) {
  // Group by Handle
  const groups = new Map();
  for (const r of rows) {
    const handle = r.Handle?.trim();
    if (!handle) continue;
    if (!groups.has(handle)) groups.set(handle, { handle, title: "", vendor: "", tags: "", variants: [] });
    const g = groups.get(handle);
    if (r.Title && !g.title) g.title = r.Title;
    if (r.Vendor && !g.vendor) g.vendor = r.Vendor;
    if (r.Tags && !g.tags) g.tags = r.Tags;
    if (r["Variant SKU"] && r["Option1 Value"]) {
      g.variants.push({ sku: r["Variant SKU"], size: r["Option1 Value"], price: r["Variant Price"] });
    }
  }
  return Array.from(groups.values());
}

function matchSeriesName(tags, vendor, seriesByMfr) {
  const known = seriesByMfr.get((vendor || "").trim().toLowerCase()) || [];
  if (!known.length) return null;
  const tagNorms = (tags || "").split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);
  for (const sn of known) if (tagNorms.includes(sn.toLowerCase())) return sn;
  for (const sn of known) {
    const snl = sn.toLowerCase();
    if (snl.length < 3) continue;
    for (const t of tagNorms) if (t.length >= 3 && (snl.includes(t) || t.includes(snl))) return sn;
  }
  return null;
}

function coerceSimpleRow(r) {
  const num = (v) => (v === "" || v == null ? null : Number(v));
  // yes/no/true/false/1/0 -> boolean; blank or absent column -> null (leave unchanged)
  const yesno = (v) => {
    if (v == null || String(v).trim() === "") return null;
    const s = String(v).trim().toLowerCase();
    if (["yes", "y", "true", "1", "discontinued"].includes(s)) return true;
    if (["no", "n", "false", "0", "active"].includes(s)) return false;
    return null;
  };
  return {
    manufacturer_name: r.manufacturer_name || "",
    series_name: r.series_name || "",
    name: r.name || "",
    base_sku: r.base_sku || "",
    color_hex: r.color_hex || null,
    inventory_type: (r.inventory_type || "rolls").toLowerCase() === "count" ? "count" : "rolls",
    full_roll_length_m: num(r.full_roll_length_m),
    discontinued: yesno(r.discontinued),
    discontinued_at: (r.discontinued_at || "").trim() || null,
    low_stock_threshold_meters: num(r.low_stock_threshold_meters) ?? 5,
    low_stock_threshold_count: num(r.low_stock_threshold_count) ?? 2,
    current_count: num(r.current_count) ?? 0,
    cost_per_meter: num(r.cost_per_meter),
    price_per_meter: num(r.price_per_meter),
    cost_per_unit: num(r.cost_per_unit),
    price_per_unit: num(r.price_per_unit),
    notes: r.notes || "",
  };
}

export default function BulkImportDialog({ onSuccess }) {
  const [open, setOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [format, setFormat] = useState("auto"); // auto | shopify | simple
  const [createRolls, setCreateRolls] = useState(true);
  const [mfgDate, setMfgDate] = useState(new Date().toISOString().slice(0, 10));
  const [skipDup, setSkipDup] = useState(true);
  const [autoCreate, setAutoCreate] = useState(true);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [resultFormat, setResultFormat] = useState(null);
  const [seriesByMfr, setSeriesByMfr] = useState(new Map());

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/manufacturers"), api.get("/series")])
      .then(([mRes, sRes]) => {
        const idToName = new Map((mRes.data || []).map((m) => [m.id, (m.name || "").trim().toLowerCase()]));
        const map = new Map();
        for (const s of sRes.data || []) {
          const key = idToName.get(s.manufacturer_id);
          if (!key) continue;
          if (!map.has(key)) map.set(key, []);
          map.get(key).push(s.name);
        }
        setSeriesByMfr(map);
      })
      .catch(() => {});
  }, [open]);

  const parsed = useMemo(() => {
    if (!csvText.trim()) return { headers: [], rows: [], detected: null, error: null };
    try {
      const { headers, rows, parseErrors } = parseCSVRows(csvText);
      const detected = detectFormat(headers);
      if (!detected) {
        return { headers, rows, detected: null, error: `Unrecognised format. Required: Shopify export (with ${SHOPIFY_HEADERS_REQUIRED.join(", ")}) OR simple CSV (with manufacturer_name, series_name, name, base_sku).` };
      }
      const fatalErrors = (parseErrors || []).filter((e) => e.type !== "FieldMismatch").slice(0, 3);
      const error = fatalErrors.length ? `CSV parse warnings: ${fatalErrors.map((e) => e.message).join("; ")}` : null;
      return { headers, rows, detected, error };
    } catch (e) { return { headers: [], rows: [], detected: null, error: String(e) }; }
  }, [csvText]);

  const fileStats = useMemo(() => {
    if (!csvText) return null;
    const lineCount = (csvText.match(/\n/g) || []).length + 1;
    const sizeKb = (new Blob([csvText]).size / 1024).toFixed(1);
    return { lineCount, sizeKb };
  }, [csvText]);

  const activeFormat = format === "auto" ? parsed.detected : format;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setCsvText(text);
  };

  const downloadTemplate = (which) => {
    const content = which === "shopify" ? SHOPIFY_TEMPLATE : SIMPLE_TEMPLATE;
    const blob = new Blob([content], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = which === "shopify" ? "shopify-products-template.csv" : "simple-products-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const onImport = async (opts = {}) => {
    setSubmitting(true);
    try {
      if (activeFormat === "shopify") {
        const { data } = await api.post("/products/import-shopify", {
          csv_text: csvText,
          create_rolls: createRolls,
          default_manufacturing_date: mfgDate,
          skip_duplicate_sku: skipDup,
        });
        setResult(data);
        setResultFormat("shopify");
        toast.success(`Imported ${data.products_created_count} products${data.rolls_created_count ? `, ${data.rolls_created_count} rolls` : ""}`);
      } else {
        const useUpdate = opts.update_existing ?? updateExisting;
        const { data } = await api.post("/products/bulk", {
          rows: parsed.rows.map(coerceSimpleRow),
          auto_create_missing: autoCreate,
          skip_duplicate_sku: opts.skip_duplicate_sku ?? skipDup,
          update_existing: useUpdate,
        });
        setResult(data);
        setResultFormat("simple");
        toast.success(`Imported: ${data.created_count} created, ${data.updated_count || 0} updated`);
      }
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  const reimportWithUpdate = () => {
    setUpdateExisting(true);
    setSkipDup(false);
    onImport({ update_existing: true, skip_duplicate_sku: false });
  };

  const reset = () => { setCsvText(""); setResult(null); setResultFormat(null); };

  const shopifyGroups = useMemo(() => activeFormat === "shopify" ? shopifyPreview(parsed.rows) : [], [parsed.rows, activeFormat]);
  const totalVariants = shopifyGroups.reduce((s, g) => s + g.variants.length, 0);
  const unmatchedCount = useMemo(
    () => (activeFormat === "shopify" ? shopifyGroups.filter((g) => !matchSeriesName(g.tags, g.vendor, seriesByMfr)).length : 0),
    [shopifyGroups, seriesByMfr, activeFormat],
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2 rounded-md" data-testid="bulk-import-btn">
          <UploadSimple size={16} /> Bulk Import
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileCsv size={20} /> Bulk Product Import <ImportInfoPopover /></DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-5 py-2">
            {/* Templates */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-mono tracking-[0.15em] uppercase text-slate-500">Templates:</span>
              <Button variant="outline" size="sm" onClick={() => downloadTemplate("shopify")} className="gap-2 rounded-md text-xs h-8" data-testid="download-shopify-template">
                <Download size={12} /> Shopify export
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadTemplate("simple")} className="gap-2 rounded-md text-xs h-8" data-testid="download-simple-template">
                <Download size={12} /> Simple CSV
              </Button>
            </div>

            <Tabs defaultValue="upload">
              <TabsList>
                <TabsTrigger value="upload" data-testid="bulk-tab-upload">Upload File</TabsTrigger>
                <TabsTrigger value="paste" data-testid="bulk-tab-paste">Paste CSV</TabsTrigger>
              </TabsList>
              <TabsContent value="upload" className="space-y-2">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Upload .csv file</Label>
                <input type="file" accept=".csv,text/csv" onChange={onFile}
                  className="block w-full text-sm border border-slate-200 rounded-md p-2 file:mr-4 file:py-2 file:px-3 file:border-0 file:text-xs file:font-medium file:bg-slate-100 hover:file:bg-slate-200"
                  data-testid="bulk-file-input" />
              </TabsContent>
              <TabsContent value="paste" className="space-y-2">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Paste CSV (with header row)</Label>
                <Textarea
                  rows={8}
                  value={csvText}
                  onChange={(e) => setCsvText(e.target.value)}
                  placeholder={SHOPIFY_TEMPLATE}
                  className="font-mono text-xs"
                  data-testid="bulk-csv-textarea"
                />
              </TabsContent>
            </Tabs>

            {/* Detection summary */}
            {parsed.error && (
              <div className="border border-red-200 bg-red-50 text-red-700 rounded-md p-3 text-sm">{parsed.error}</div>
            )}
            {fileStats && (
              <div className="grid grid-cols-3 gap-0 border border-slate-200 bg-white text-xs" data-testid="bulk-stats">
                <div className="p-3 border-r border-slate-200">
                  <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">File size</div>
                  <div className="font-mono text-base font-semibold mt-0.5 tabular-nums">{fileStats.sizeKb} KB</div>
                </div>
                <div className="p-3 border-r border-slate-200">
                  <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Lines in CSV</div>
                  <div className="font-mono text-base font-semibold mt-0.5 tabular-nums">{fileStats.lineCount.toLocaleString()}</div>
                </div>
                <div className="p-3">
                  <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Rows parsed</div>
                  <div className="font-mono text-base font-semibold mt-0.5 tabular-nums">{parsed.rows.length.toLocaleString()}</div>
                </div>
              </div>
            )}
            {activeFormat && !parsed.error && (
              <div className="border border-slate-200 bg-slate-50 rounded-md p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Detected format</div>
                    <div className="font-heading text-base font-semibold mt-0.5">
                      {activeFormat === "shopify" ? "Shopify product export" : "Simple product CSV"}
                    </div>
                  </div>
                  <div className="text-right">
                    {activeFormat === "shopify" ? (
                      <>
                        <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Will create</div>
                        <div className="font-mono text-sm font-semibold mt-0.5">
                          {shopifyGroups.length} products · {totalVariants} variants
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Rows detected</div>
                        <div className="font-mono text-sm font-semibold mt-0.5">{parsed.rows.length}</div>
                      </>
                    )}
                  </div>
                </div>

                {activeFormat === "shopify" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-slate-200">
                    <div className="flex items-center justify-between bg-white border border-slate-200 rounded-md p-2.5">
                      <div className="pr-3">
                        <div className="text-xs font-medium">Also create rolls</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">One roll per variant size with parsed length</div>
                      </div>
                      <Switch checked={createRolls} onCheckedChange={setCreateRolls} data-testid="shopify-create-rolls" />
                    </div>
                    <div className="bg-white border border-slate-200 rounded-md p-2.5">
                      <Label className="text-[10px] font-mono tracking-[0.15em] uppercase text-slate-500">Manufacturing date for new rolls</Label>
                      <Input type="date" value={mfgDate} onChange={(e) => setMfgDate(e.target.value)} className="font-mono text-xs h-8 mt-1" data-testid="shopify-mfg-date" />
                    </div>
                  </div>
                )}

                {activeFormat === "simple" && (
                  <div className="space-y-3 pt-2 border-t border-slate-200">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center justify-between bg-white border border-slate-200 rounded-md p-2.5">
                        <div className="text-xs">Auto-create missing</div>
                        <Switch checked={autoCreate} onCheckedChange={setAutoCreate} />
                      </div>
                      <div className="flex items-center justify-between bg-white border border-slate-200 rounded-md p-2.5">
                        <div className="text-xs">Skip duplicates</div>
                        <Switch checked={skipDup} onCheckedChange={setSkipDup} disabled={updateExisting} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between bg-white border border-slate-200 rounded-md p-2.5">
                      <div className="pr-3">
                        <div className="text-xs font-medium">Update existing products</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">Matches by SKU and updates cost, price, thresholds, colour &amp; notes instead of skipping.</div>
                      </div>
                      <Switch checked={updateExisting} onCheckedChange={setUpdateExisting} data-testid="update-existing-switch" />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Preview */}
            {activeFormat === "shopify" && unmatchedCount > 0 && (
              <div className="border border-amber-200 bg-amber-50 text-amber-800 rounded-md p-3 text-sm" data-testid="series-match-warning">
                <strong>{unmatchedCount}</strong> product{unmatchedCount > 1 ? "s" : ""} have no matching series for their brand in the Tags column — these will be <strong>skipped with an error</strong>. Series is matched against each brand's existing series (e.g. 3M → 2080 / 1080). Add the series to the brand first, or fix the Tags.
              </div>
            )}
            {activeFormat === "shopify" && shopifyGroups.length > 0 && (
              <div className="border border-slate-200 rounded-md">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-mono tracking-[0.15em] uppercase text-slate-600 flex justify-between">
                  <span>Products preview</span>
                  <span>showing first 8 of {shopifyGroups.length}</span>
                </div>
                <div className="overflow-x-auto max-h-72 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                      <tr className="text-left">
                        <th className="px-3 py-2">Vendor</th>
                        <th className="px-3 py-2">Title (color)</th>
                        <th className="px-3 py-2">Series (matched)</th>
                        <th className="px-3 py-2 text-right">Variants</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shopifyGroups.slice(0, 8).map((g) => {
                        const series = matchSeriesName(g.tags, g.vendor, seriesByMfr);
                        const colorName = g.title.startsWith(g.vendor) ? g.title.slice(g.vendor.length).trim() : g.title;
                        return (
                          <tr key={g.handle} className="border-t border-slate-100">
                            <td className="px-3 py-2">{g.vendor}</td>
                            <td className="px-3 py-2 font-medium">{colorName}</td>
                            <td className="px-3 py-2">
                              {series ? (
                                <span className="text-emerald-700 font-medium">{series}</span>
                              ) : (
                                <span className="text-red-600 font-medium">No match — will error</span>
                              )}
                            </td>
                            <td className="px-3 py-2 font-mono text-right">{g.variants.length}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {activeFormat === "simple" && parsed.rows.length > 0 && (
              <div className="border border-slate-200 rounded-md">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-mono tracking-[0.15em] uppercase text-slate-600 flex justify-between">
                  <span>Preview</span>
                  <span>showing first 5 of {parsed.rows.length}</span>
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left">
                      <th className="px-2 py-1.5">Mfr</th><th className="px-2 py-1.5">Series</th>
                      <th className="px-2 py-1.5">Name</th><th className="px-2 py-1.5">SKU</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.rows.slice(0, 5).map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1.5">{r.manufacturer_name}</td>
                        <td className="px-2 py-1.5">{r.series_name}</td>
                        <td className="px-2 py-1.5">{r.name}</td>
                        <td className="px-2 py-1.5 font-mono">{r.base_sku}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <ResultPanel result={result} format={resultFormat} onReimportUpdate={reimportWithUpdate} reimporting={submitting} />
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-md">Cancel</Button>
              <Button
                onClick={() => onImport()}
                disabled={submitting || !activeFormat || parsed.rows.length === 0}
                className="bg-blue-600 hover:bg-blue-700 rounded-md gap-2"
                data-testid="bulk-import-confirm"
              >
                <UploadSimple size={16} /> {submitting ? "Importing…" : "Import"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={reset} className="rounded-md" data-testid="bulk-import-another">Import another</Button>
              <Button onClick={() => setOpen(false)} className="bg-blue-600 hover:bg-blue-700 rounded-md">Done</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResultPanel({ result, format, onReimportUpdate, reimporting }) {
  if (format === "shopify") {
    return (
      <div className="space-y-4 py-2">
        <div className="grid grid-cols-4 gap-0 border border-slate-200">
          <Stat icon={<CheckCircle weight="fill" size={18} className="text-emerald-600" />} label="Products" value={result.products_created_count} accent="text-emerald-700" />
          <Stat icon={<CheckCircle weight="fill" size={18} className="text-blue-600" />} label="Rolls" value={result.rolls_created_count} accent="text-blue-700" />
          <Stat icon={<MinusCircle weight="fill" size={18} className="text-slate-500" />} label="Skipped" value={result.products_skipped_count + result.rolls_skipped_count} />
          <Stat icon={<XCircle weight="fill" size={18} className="text-red-600" />} label="Errors" value={result.error_count} accent="text-red-600" last />
        </div>
        <ErrorList errors={result.errors} />
      </div>
    );
  }
  const allSkipped = (result.created_count || 0) === 0 && (result.updated_count || 0) === 0 && (result.skipped_count || 0) > 0;
  return (
    <div className="space-y-4 py-2">
      <div className="grid grid-cols-4 gap-0 border border-slate-200">
        <Stat icon={<CheckCircle weight="fill" size={18} className="text-emerald-600" />} label="Created" value={result.created_count} accent="text-emerald-700" />
        <Stat icon={<CheckCircle weight="fill" size={18} className="text-blue-600" />} label="Updated" value={result.updated_count || 0} accent="text-blue-700" />
        <Stat icon={<MinusCircle weight="fill" size={18} className="text-slate-500" />} label="Skipped" value={result.skipped_count} />
        <Stat icon={<XCircle weight="fill" size={18} className="text-red-600" />} label="Errors" value={result.error_count} accent="text-red-600" last />
      </div>
      {allSkipped && (
        <div className="border border-amber-300 bg-amber-50 rounded-md p-3.5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="all-skipped-hint">
          <Warning size={20} weight="fill" className="text-amber-600 shrink-0" />
          <div className="text-sm text-amber-900 flex-1">
            All <strong>{result.skipped_count}</strong> rows already exist, so nothing was changed. To apply your edits (pricing, thresholds, colours, notes), turn on <strong>“Update existing products”</strong> and import again.
          </div>
          <Button onClick={onReimportUpdate} disabled={reimporting} className="bg-amber-600 hover:bg-amber-700 text-white rounded-md gap-2 shrink-0" data-testid="reimport-update-btn">
            <UploadSimple size={15} /> {reimporting ? "Updating…" : "Update existing & import again"}
          </Button>
        </div>
      )}
      {(result.updated_count || 0) > 0 && (
        <div className="flex items-start gap-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="inventory-safe-note">
          <Info size={14} className="text-slate-400 shrink-0 mt-0.5" />
          <span>Only catalogue details were updated. Your <strong>roll inventory and stock counts were not changed</strong> by this import.</span>
        </div>
      )}
      <ErrorList errors={result.errors} />
      <ChangeList updated={result.updated} />
    </div>
  );
}

function fmtChangeVal(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return v.toString();
  return String(v);
}

function ImportInfoPopover() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center h-6 w-6 rounded-full text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
          aria-label="What happens when I import?"
          data-testid="import-info-btn"
        >
          <Info size={18} weight="bold" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 text-sm" data-testid="import-info-popover">
        <div className="font-semibold text-slate-800 mb-1.5">What this import does</div>
        <ul className="space-y-1.5 text-[13px] text-slate-600 leading-relaxed list-disc pl-4">
          <li>Matches your rows to existing products by <strong>Base SKU</strong>.</li>
          <li>With <strong>“Update existing products”</strong> on, it updates catalogue details: name, colour, pricing, low-stock thresholds, discontinued status &amp; notes.</li>
          <li>After importing you'll see a <strong>“What changed”</strong> list showing each product's old&nbsp;→&nbsp;new values, so everyone can see exactly what was edited.</li>
          <li>It <strong>never changes your roll inventory or stock counts</strong> — physical stock is only ever changed in Inventory.</li>
          <li>New SKUs are created; rows are skipped when the toggle is off.</li>
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function ChangeList({ updated }) {
  const withChanges = (updated || []).filter((u) => u.changes && u.changes.length > 0);
  if (withChanges.length === 0) return null;
  return (
    <div data-testid="import-changes-list">
      <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">What changed</div>
      <div className="border border-blue-200 rounded-md max-h-56 overflow-y-auto">
        {withChanges.map((u, i) => (
          <div key={i} className="px-3 py-2 border-b last:border-0 border-blue-100" data-testid={`import-change-${u.sku}`}>
            <div className="text-xs font-semibold text-slate-800">
              <span className="font-mono text-slate-500">{u.sku}</span>{u.name ? ` · ${u.name}` : ""}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {u.changes.map((c, j) => (
                <span key={j} className="text-[11px] font-mono bg-blue-50 border border-blue-200 text-blue-800 rounded-sm px-1.5 py-0.5">
                  {c.field}: <span className="text-slate-400 line-through">{fmtChangeVal(c.from)}</span> → <span className="font-semibold">{fmtChangeVal(c.to)}</span>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ icon, label, value, accent, last }) {
  return (
    <div className={`p-4 ${last ? "" : "border-r border-slate-200"}`}>
      <div className="flex items-center gap-2">{icon}</div>
      <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mt-1">{label}</div>
      <div className={`font-mono text-3xl font-semibold mt-1 ${accent || "text-slate-900"} tabular-nums`}>{value}</div>
    </div>
  );
}

function ErrorList({ errors }) {
  if (!errors?.length) return null;
  return (
    <div>
      <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Errors</div>
      <div className="border border-red-200 rounded-md max-h-48 overflow-y-auto">
        {errors.map((e, i) => (
          <div key={i} className="px-3 py-2 border-b last:border-0 border-red-100 text-xs">
            <span className="font-mono text-slate-500">{e.row ? `Row ${e.row}` : e.handle ? `Handle ${e.handle}` : "—"}:</span>{" "}
            <span className="font-mono">{e.sku || ""}</span>{" "}
            <span className="text-red-700">{e.error}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
